<template>
  <div
    class="search"
    :class="{ 'search--active': searchDialogOpen }"
  >
    
<FormKitLazyProvider>
<div
      class="backdrop"
      :class="{ 'backdrop--active': searchDialogOpen }"
      @click="closeDialog()"
    />

    <div class="search__panel">
      <FormKit
        id="search__form"
        name="search__form"
        form-class="search__form"
        type="form"
        :incomplete-message="$t('Sorry, not all fields are filled out correctly.')"
        :actions="false"
        @submit="doSearch"
      >
        <label
          for="searchInput"
          class="visually-hidden"
        >
          {{ $t('Search for products') }}
        </label>
        <input
          id="searchInput"
          ref="searchInput"
          v-model="search"
          type="search"
          class="formkit-input"
          data-testid="quicksearch"
          :placeholder="searchPlaceholder"
          @focus="openDialog()"
        >

        <button
          type="submit"
          class="search__submit"
        >
          <IconSearch class="icon icon--search" />
        </button>

        <button
          v-if="search.length > 0"
          type="button"
          class="search__reset"
          @click="search = ''"
        >
          <IconClose class="icon icon--close" />
        </button>

        <span
          v-if="search.length > 0"
          class="search__divider"
        />

        <button
          type="button"
          class="search__close"
          @click="closeDialog()"
        >
          <IconCssArrow2 class="icon icon--arrow icon--arrow-left" />
        </button>
      </FormKit>

      <div class="search__results">
        <div v-if="search.length > 3 && !hasResults && !loading">
          {{ $t('Unfortunately we were not able to find any results for your search') }}
        </div>
        <div v-else-if="hasResults">
          <div
            v-for="(block) in suggestionBlocks"
            :key="block"
            class="search__section"
          >
            <div class="search__heading heading heading--6">
              {{ block?.name }}
            </div>
            <ul class="search__list">
              <li
                v-for="suggestion in block.suggestions"
                :key="suggestion"
                class="search__item"
              >
                <AppLink
                  :to="getnavigationLink(suggestion, block?.type)"
                  class="search__link"
                  @click="closeDialog()"
                >
                  <span>
                    <span class="search__label">
                      <UiRichtext :html="highlight(suggestion.match)" />
                    </span>

                    <span
                      v-if="suggestion?.navigationLink?.context?.category?.parentName"
                      class="search__parent"
                    >
                      in {{ suggestion.navigationLink?.context?.category?.parentName }}
                    </span>
                  </span>
                  <IconCssArrow2 class="icon icon--arrow" />
                </AppLink>
              </li>
            </ul>
          </div>

          <template
            v-for="(block) in itemBlocks"
            :key="block.name"
          >
            <div
              v-if="block.items.length"
              class="search__section"
            >
              <div class="search__heading heading heading--6">
                {{ block?.name }}
              </div>
              <ul class="search__list">
                <li
                  v-for="item in block.items"
                  :key="item"
                  class="search__item"
                >
                  <AppLink
                    :to="item.url"
                    class="search__link"
                    @click="closeDialog()"
                  >
                    <span>
                      <span class="search__label">
                        {{ item.title }}
                      </span>
                    </span>
                    <IconCssArrow2 class="icon icon--arrow" />
                  </AppLink>
                </li>
              </ul>
            </div>
          </template>

          <div class="search__section">
            <ul class="search__products">
              <li
                v-for="(product, index) in searchProducts"
                :key="product.id"
                class="search__item"
              >
                <AppLink
                  :to="getProductUrl(product)"
                  class="search-product"
                  :class="{
                    'sqzl-click': product?.squeezely,
                  }"
                  :data-sqzl-personalization-id="product?.squeezely?.personalizationId"
                  :data-sqzl-variant-id="product?.squeezely?.variantId"
                  :data-sqzl-variant-revision="product?.squeezely?.variantRevision"
                  @click="handleProductClick(product, index)"
                >
                  <img
                    class="search-product__image"
                    :src="getProductImage(product, index)"
                    width="46"
                    height="46"
                    loading="lazy"
                    fetchpriority="low"
                    decoding="async"
                    alt=""
                    aria-hidden="true"
                  >
                  <span class="search-product__title">
                    {{ product.name }}
                  </span>
                </AppLink>
              </li>
            </ul>
          </div>
        </div>

        <div v-else-if="recentSearches.length > 0">
          <div class="search__heading heading heading--6">
            {{ $t('Recent search terms') }}
          </div>

          <ul class="search__list">
            <li
              v-for="recentSearch in recentSearches"
              :key="recentSearch"
              class="search__item"
            >
              <button
                type="button"
                class="search__link"
                @click="setSearch(recentSearch)"
              >
                <span>{{ recentSearch }}</span>
                <IconCssArrow2 class="icon icon--arrow" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
</FormKitLazyProvider>

  </div>
</template>

<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
import type { DataLayerItem } from '~/composables/useDataLayer'
import layoutQuery from '~layers/app/graphql/layout.gql'

const {
  data,
  search,
  searchDialogOpen,
  recentSearches,
  loading,
  products,
  itemBlocks,
  suggestionBlocks,
  searchRedirect,
} = useQuickSearch()

const { breakpoint } = useApp()

const { public: config } = useRuntimeConfig()
const { data: layoutData } = await useAsyncQuery(layoutQuery, { locale: config.strapiLocale })

const { lastRequestId, sessionKey, profileKey, track: trackTweakwise } = useTweakwise()
const dataLayerOverrides = computed<Partial<DataLayerItem>>(() => ({ item_list_name: 'quick-search', item_list_id: search.value }))

const searchInput = useTemplateRef('searchInput')

const searchPlaceholders = ref<Record<string, string>>({
  desktop: layoutData.value?.copies?.find((item: never) => item.UID === 'search__desktop')?.Richtexts?.[0]?.Richtext?.[0]?.children?.[0]?.text || '',
  mobile: layoutData.value?.copies?.find((item: never) => item.UID === 'search__mobile')?.Richtexts?.[0]?.Richtext?.[0]?.children?.[0]?.text || '',
})

const searchPlaceholder = ref<string>(searchPlaceholders.value.desktop as string)

const recentSearch = ref<string>('')

const totalItems = computed(() => {
  return itemBlocks.value.reduce((acc: number, item: any) => {
    return acc + item.nrOfItems
  }, 0)
})

const totalSuggestions = computed(() => {
  return suggestionBlocks.value.reduce((acc: number, item: any) => {
    return acc + item.suggestions.length
  }, 0)
})

const searchProducts = computed(() => {
  return products.value
    .filter((product: any) => product?.name && product?.url && product?.image)
    .slice(0, 3)
})

const hasResults = computed(() => {
  return totalItems.value > 0 || totalSuggestions.value > 0 || searchProducts.value.length > 0
})

function getSearchValueLink(to: string) {
  return '/' + to + '/?q=' + search.value
}
function getnavigationLink(suggestion: any, suggestionType: string) {
  if (suggestion.redirects?.length) {
    return suggestion.redirects?.[0]?.url
  }
  if (suggestion.navigationLink?.context?.category?.link) {
    const url = new URL(suggestion.navigationLink?.context?.category?.link)
    let urlPath = url.pathname.replace('.html', '').trim()
    if (urlPath.startsWith('/')) {
      urlPath = urlPath.substring(1)
    }
    return getSearchValueLink(urlPath)
  }
  else if (suggestionType === 'FacetFilter') {
    const brandFilter = suggestion.navigationLink?.context?.facetFilters?.find((facetFilter: never) => facetFilter?.key === 'tra_brand')
    const airlineFilter = suggestion.navigationLink?.context?.facetFilters?.find((facetFilter: never) => facetFilter?.key === 'tra_airline')
    if (brandFilter) {
      return `/${stringToSlug(brandFilter.values[0] || '')}/?q=${suggestion.match}`
    }
    if (airlineFilter) {
      return `/koffers/handbagage-koffers/?tra_airline=${airlineFilter.values[0] || ''}&q=${suggestion.match}`
    }
  }
  return '/search/?q=' + suggestion.match
}

watchEffect(() => {
  if (search.value.length > 3 && hasResults.value) {
    searchDialogOpen.value = true
  }
})

watch(search, () => {
  if (search.value !== recentSearch.value) {
    recentSearch.value = ''
  }
  if (!search.value) {
    // searchDialogOpen.value = false
    data.value = null
  }
})

const setSearch = (newSearch: string) => {
  recentSearch.value = newSearch
  search.value = newSearch
}

const closeDialog = () => {
  searchDialogOpen.value = false
  search.value = ''
}

const openDialog = () => {
  searchDialogOpen.value = true
}

const onKeyPress = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    // trackClose()
    closeDialog()
  }
}

const doSearch = () => {
  setTimeout(() => {
    searchRedirect()
    closeDialog()
    searchInput.value.blur()
  }, 600)
}

const highlight = (label: string) => {
  return label.replace(search.value, `<mark class="search__highlight">${search.value}</mark>`)
}

const { selectItem } = useDataLayer()

const trackProductClick = (product: any, index: number) => {
  selectItem(product, {
    ...dataLayerOverrides.value,
    index,
  })

  trackTweakwise('itemclick', {
    RequestId: lastRequestId.value,
    ItemId: product.tweakwiseId,
    ProfileKey: profileKey.value,
    SessionKey: sessionKey.value,
  })
}

const getProductUrl = (product: any) => {
  const separator = product.url.includes('?') ? '&' : '?'

  return `${product.url}${separator}q=${encodeURIComponent(search.value)}`
}

const getProductImage = (product: any, index: number) => {
  return getResizedProductImage(
    product.image,
    product.name,
    {
      width: 92,
      height: 92,
      imageSize: product.imageSize,
      index: index + 1,
    },
  )
}

const handleProductClick = (product: any, index: number) => {
  trackProductClick(product, index)
  closeDialog()

  if (product?.squeezely && product.url) {
    navigateTo(getProductUrl(product))
  }
}

onMounted(() => {
  window.addEventListener('keyup', onKeyPress)

  watchEffect(() => {
    if (['mobile', 'phablet', 'tablet'].includes(breakpoint.value)) {
      searchPlaceholder.value = searchPlaceholders.value?.mobile
    }
    else {
      searchPlaceholder.value = searchPlaceholders.value?.desktop
    }
  })
})

onUnmounted(() => {
  window.removeEventListener('keyup', onKeyPress)
})
</script>

<style>
@import url("~layers/app/components/App/QuickSearch.css");
</style>
